import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ResultsGuidedService } from 'src/app/services/learner/results-guided-service';
import { IResultsGuided } from 'src/app/interfaces/iresults-guided';
import { IPathwayQuizResult } from 'src/app/interfaces/ipathway-quiz-result';
import { Observable } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatMenuTrigger } from '@angular/material/menu';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { AppConstants } from 'src/app/app-constants';
import { isComplianceOnly, showsFeedbackWhenOutOfCompliance } from 'src/app/utils/constants/self-serve/compliance-score-impact.constants';
import { ResponseTimeService } from 'src/app/services/general/response-time.service';
import { CallFlagService } from 'src/app/services/learner/call-flag/call-flag.service';
import { AssignmentService } from 'src/app/services/learner/assignment.service';
import { IPracticeCallResult } from 'src/app/interfaces/ipractice-call-result';
import { IAssignmentModule } from 'src/app/interfaces/iassignment-module';
import { MatSelectChange } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { take } from 'rxjs';
import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { TranslationService } from 'src/app/services/translation.service';
import { IComplianceScoring } from 'src/app/interfaces/icompliance-scoring';
import { ResultsConversationalService } from 'src/app/services/learner/results-conversational.service';
import { IAssignmentModuleCall } from 'src/app/interfaces/iassignment-module-call';
import { ICallDetailModal } from 'src/app/interfaces/icall-detail-modal';
import { ImageViewerComponent } from 'src/app/pages/general/image-viewer/image-viewer.component';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { CallDetailsModalComponent } from 'src/app/pages/learner/assignment/call-details-modal/call-details-modal.component';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';
import { SplitIoService } from 'src/app/services/external-tools/split-io.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ImagePreloaderService } from 'src/app/services/learner/image-preloader/image-preloader.service';
import { IPreloadedImageResult } from 'src/app/interfaces/ipreloaded-image-result';
import { CallStoreService } from 'src/app/pages/learner/call/library/call-store/call-store.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AccountFeatureService } from 'src/app/services/account-feature/account-feature.service';

@Component({
  selector: 'app-results-visual-aid',
  standalone: false,
  // Out of compliance, the page uses the narrower Figma column
  host: { '[class.out-of-compliance]': 'isUserCompliant === false && !isEmbeddedInQuizResult' },
  templateUrl: './results-visual-aid.component.html',
  styleUrls: ['./results-visual-aid.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'results',
    },
  ],
})
export class ResultsVisualAidComponent implements OnInit, OnDestroy {
  @Input() isEmbeddedInQuizResult = false;
  @Input() embeddedStepsToReview: string[] | null | undefined = [];
  @Output() avatarThumbnailReady = new EventEmitter<string>();
  @ViewChild(MatMenuTrigger) tooltipMenu!: MatMenuTrigger;
  isExpanded: boolean = false;
  resultReady: boolean = false;
  private pollingInterval: NodeJS.Timeout = setTimeout(() => { }, 0);
  msDuration: number = 90 * 1000;
  startTime: number = 0;
  practiceScore: number = 0;
  criteriaScore: number = 0;
  callScoreCalcType: string = '';

  // Guided Results hang tight message
  isErrorLoadingScreen: boolean = false;
  displayButtonLoadingScreen: boolean = true;
  titleLoadingScreen: string = '';
  messageLoadingScreen: string = '';
  guidedResults: (IResultsGuided | IPathwayQuizResult) | undefined = undefined;
  isFetchingResults: boolean = false;
  showSkillResults: boolean = false;
  showTranscription: boolean = false;
  indicator: boolean = false;
  tabIndex: number = 0;
  typePractice: string = AppConstants.TYPE_PRACTICE;
  typeCertification: string = AppConstants.TYPE_CERTIFICATION;
  calcTypeRatio5pt0min: string = AppConstants.CALL_RATIO_5PT_0MIN;
  promptTypeCleanTranscript: string = AppConstants.CLEAN_TRANSCRIPT_PROMPT_TYPE;
  practiceType: string = '';
  showMissedFacts: boolean = false;
  visualAid: boolean = false;
  isMaxAttemptsReached: boolean = false;
  practiceCallResults: IPracticeCallResult[] = [];
  practiceCallUuid: string = '';
  callUuid: string = '';
  assignment: IAssignmentModule | undefined;
  positiveCount: number = 0;
  totalContextCount: number = 0;
  expandedIndices: number[] = [];
  imageSrc: string =
    'https://images.pexels.com/photos/28144558/pexels-photo-28144558.jpeg?cs=srgb&dl=pexels-stevedoessteve-28144558.jpg&fm=jpg';
  call: IAssignmentModuleCall | undefined;
  callDetails: ICallDetailModal | null = null;
  @ViewChild('callDetailsModal', { static: true, read: CdkPortalOutlet })
  callDetailsModalPortalOutlet!: CdkPortalOutlet;
  componentPortal: ComponentPortal<CallDetailsModalComponent> | null = null;
  assignmentDaysLeft: number | null = null;
  imgArray: string[] = [];
  scrollStrategy: ScrollStrategy | null = null;
  isUserCompliant: boolean | null = true;
  complianceScoreImpactEnabled = false;
  complianceScoringResult: IComplianceScoring | undefined;
  isNonCompliantFeedbackExpanded: boolean = true;
  anyCorrectFact: boolean = false;
  translationLoaded: boolean = false;
  AppConstants: typeof AppConstants = AppConstants;
  customResultMessage: string | null = null;
  isPathwayFlow: boolean = false;
  pathwayName: string = '';
  stepName: string = '';
  private isQuizLoadingStateEnabled: boolean = false;
  private logger = inject(LoggerService);

  constructor(
    private resultsGuidedService: ResultsGuidedService,
    private router: Router,
    private route: ActivatedRoute,
    private clipboard: Clipboard,
    private gaService: GoogleAnalyticsService,
    private responseTimeService: ResponseTimeService,
    public callFlagService: CallFlagService,
    private cdr: ChangeDetectorRef,
    private assignmentService: AssignmentService,
    private dialog: MatDialog,
    private overlay: Overlay,
    private amplitudeService: AmplitudeService,
    private translationService: TranslationService,
    private resultsConversationalService: ResultsConversationalService,
    protected localeService: LocaleService,
    private imagePreloaderService: ImagePreloaderService,
    private splitIoService: SplitIoService,
    private callStore: CallStoreService,
    private featureFlagsService: FeatureFlagsService,
    private accountFeatureService: AccountFeatureService
  ) { }

  async ngOnInit(): Promise<void> {
    this.translationService.loadScopedTranslation('results').then(() => {
      this.titleLoadingScreen = this.translationService.translate(
        'PROCESSING_CALL',
        'results'
      );
      this.messageLoadingScreen = `${this.translationService.translate(
        'HANG_TIGHT',
        'results'
      )} <br> ${this.translationService.translate(
        'VIEW_YOUR_RESULTS',
        'results'
      )}`;
      this.translationLoaded = true;
    });
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.gaService.trackPageView(event.urlAfterRedirects);
      }
    });
    // Requested before the results so it is usually known when they arrive
    const complianceScoreImpactFlag = this.featureFlagsService.isFlagEnabled(AppConstants.COMPLIANCE_SCORE_IMPACT);
    this.pollData();
    this.callUuid = this.route.snapshot.queryParamMap.get('callUuid') || '';

    // get call flags by callUuid from local storage
    const flags = this.callFlagService.getCallFlagsLS(this.callUuid);

    this.visualAid = !!flags?.visualAidEnabled;
    this.logger.log('[CALL] results visual aid - display visual aid', { visualAid: this.visualAid });

    this.complianceScoreImpactEnabled = await complianceScoreImpactFlag;
    this.isQuizLoadingStateEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.PATHWAY_QUIZ_RESULTS_LOADING_STATE
    );

    this.cdr.detectChanges();
  }

  /** Out of compliance with the "Include feedback" setting: the other feedback still shows. */
  get feedbackWhenOutOfCompliance(): boolean {
    return (
      this.isUserCompliant === false &&
      showsFeedbackWhenOutOfCompliance(this.guidedResults?.complianceScoreImpact, this.complianceScoreImpactEnabled)
    );
  }

  /** The "Compliance only" setting: the missed guidelines say no score was given. */
  get complianceOnly(): boolean {
    return isComplianceOnly(this.guidedResults?.complianceScoreImpact, this.complianceScoreImpactEnabled);
  }

  /** Feedback sections show for compliant calls, and out of compliance with the "Include feedback" setting. */
  get showFeedback(): boolean {
    return !!this.isUserCompliant || this.feedbackWhenOutOfCompliance;
  }

  ngOnDestroy() {
    if (this.callDetailsModalPortalOutlet) {
      if (this.callDetailsModalPortalOutlet.hasAttached()) {
        this.callDetailsModalPortalOutlet.detach();
      }
      if (this.scrollStrategy) {
        this.scrollStrategy.disable();
      }
    }
    this.stopPolling();
    this.imagePreloaderService.clearCache();
  }

  toggleCollapse(event: Event, questionUuid: string) {
    const caretImgElement = event.target as HTMLImageElement;
    const questionSuggestionPElement = document.getElementById(
      'question-suggestion-' + questionUuid
    ) as HTMLElement;

    if (questionSuggestionPElement.classList.toggle('d-none')) {
      // Collapsed
      caretImgElement.src = '/assets/icons/arrow/icon-chevron-right.svg';
    } else {
      // Expanded
      caretImgElement.src = '/assets/icons/arrow/icon-chevron-down.svg';
    }
  }

  checkPollingCompletion() {
    if (this.resultReady) {
      this.stopPolling(); // Stop polling once both result is ready
    }
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  pollData() {
    this.getGuidedResults();
    this.startTime = Date.now(); // Track the start time
    this.pollingInterval = setInterval(() => {
      const elapsedTime = Date.now() - this.startTime; // Calculate the elapsed time
      // After 1 minute we display longer than expected message
      if (elapsedTime >= 1 * this.msDuration) {
        this.stopPolling();
        if (this.isQuizLoadingStateEnabled && this.isPathwayFlow) {
          this.messageLoadingScreen = this.translationService.translate(
            'QUIZ_RESULTS_LOADING',
            'results'
          );
          this.callStore.emitQuizResultPollingTimeout();
        } else {
          this.titleLoadingScreen = this.translationService.translate(
            'NEED_MORE_TIME',
            'results'
          );
          this.messageLoadingScreen = `${this.translationService.translate(
            'REFRESH_INSTRUCTION',
            'results'
          )} <br> ${this.translationService.translate(
            'RESULTS_IN_FEW_MINUTES',
            'results'
          )}`;
          this.isErrorLoadingScreen = true;
          this.displayButtonLoadingScreen = true;
          console.log('Polling stopped, display error message.');
          this.callStore.emitQuizResultDataError();
        }
      } else {
        if (!this.resultReady && !this.isFetchingResults) {
          this.getGuidedResults();
        }
      }
    }, 5000);
  }

  getGuidedResults() {
    if (this.practiceCallUuid === '') {
      this.practiceCallUuid =
        this.route.snapshot.queryParamMap.get('practiceCallUuid')!;
    }

    if (this.practiceCallUuid === '') {
      this.isFetchingResults = false;
      return;
    }

    const practiceTypeFromQuery = this.route.snapshot.queryParamMap.get('practiceType');
    if (practiceTypeFromQuery) {
      this.practiceType = practiceTypeFromQuery;
    }

    const pathwayNameFromQuery = this.route.snapshot.queryParamMap.get('pathwayName');
    if (pathwayNameFromQuery) {
      this.pathwayName = pathwayNameFromQuery;
    }

    const stepNameFromQuery = this.route.snapshot.queryParamMap.get('stepName');
    if (stepNameFromQuery) {
      this.stepName = stepNameFromQuery;
    }

    this.isPathwayFlow = this.practiceType === AppConstants.TYPE_PATHWAY;

    const shouldUsePathwayApi = this.practiceType === AppConstants.TYPE_PATHWAY;

    const results$ = (shouldUsePathwayApi
      ? this.resultsGuidedService.getPathwayQuizResultData(this.practiceCallUuid)
      : this.resultsGuidedService.getResultsGuidedData(this.practiceCallUuid)
    ) as unknown as Observable<IResultsGuided | IPathwayQuizResult>;

    this.isFetchingResults = true;
    this.responseTimeService
      .logResponseTime(results$, 'getGuidedResults')
      .subscribe({
        next: (res: IResultsGuided | IPathwayQuizResult) => {
          this.isFetchingResults = false;
          this.guidedResults = res;
          if (res?.practiceCall?.isCompliant != null) {
            this.isUserCompliant = res.practiceCall.isCompliant;
            this.getComplianceScore();
          } else {
            this.isUserCompliant = true;
          }
          if (this.guidedResults) {
            if (this.isEmbeddedInQuizResult || this.isPathwayFlow) {
              this.callStore.setQuizResultReady(true);
              this.callStore.setQuizResultData(this.guidedResults as IPathwayQuizResult);
              this.callStore.emitQuizResultDataReady();
            }
            this.avatarThumbnailReady.emit((this.guidedResults as any).avatarThumbnail ?? '');
            // clearing it for when practice call is changed from drop down
            this.positiveCount = 0;
            this.totalContextCount = 0;
            this.guidedResults.questions.forEach((question) => {
              question.practiceCallGuided.positiveContext =
                question.practiceCallGuided.positiveContext.filter(
                  (context) => context.trim() !== ''
                );
              question.practiceCallGuided.negativeContext =
                question.practiceCallGuided.negativeContext.filter(
                  (context) => context.trim() !== ''
                );
              // Set default expanded state to false for accordions
              question.isUserResponseExpanded = false;
              question.isRecommendedResponseExpanded = false;

              // calculation
              const pos = question.practiceCallGuided.positiveContext.length;
              const neg = question.practiceCallGuided.negativeContext.length;
              this.positiveCount += pos;
              this.totalContextCount += pos + neg;
            });
            if (!this.callUuid && this.guidedResults.callUuid) {
              // Pathway "View Feedback" navigation doesn't pass callUuid as a
              // query param, so the visual-aid local-storage flag lookup falls
              // back to the callUuid the API itself returns for this result.
              this.callUuid = this.guidedResults.callUuid;
              const fallbackFlags = this.callFlagService.getCallFlagsLS(this.callUuid);
              this.visualAid = !!fallbackFlags?.visualAidEnabled;
            }
            this.preloadImages();
            this.practiceScore = this.guidedResults?.practiceCall?.score;
            this.callScoreCalcType = this.guidedResults?.callScoreCalcType || '';

            const stepRequirement =
              this.isPathwayFlow && 'stepCompletionRequirement' in this.guidedResults
                ? this.guidedResults.stepCompletionRequirement
                : null;

            this.criteriaScore =
              stepRequirement?.conditionValue ??
              this.guidedResults?.moduleAssignmentScoreCriteria;
            this.practiceType = this.guidedResults?.practiceCall?.practiceType;
            // Check if guidedResults has the key promptTypes
            this.showSkillResults = this.checkPromptType();
            this.showMissedFacts = this.guidedResults?.showMissedFacts;
            this.loadCustomResultMessage();
            if (!this.isPathwayFlow && this.guidedResults?.moduleAssignmentUuid) {
              this.fetchAssignment(this.guidedResults.moduleAssignmentUuid);
            }

            this.fetchGuidedCallResults();
            this.checkTranscriptTabFeatureFlag();
            this.toggleAllInitially();
          }
          this.checkPollingCompletion();
        },
        error: (error) => {
          this.isFetchingResults = false;
          console.error('Error:', error);
          if (error.status == 500) {
            this.checkPollingCompletion();
            this.resultReady = false;
          }
        },
      });
  }

  fetchGuidedCallResults(): void {
    if (
      !this.guidedResults?.callUuid ||
      !this.guidedResults?.moduleAssignmentUuid
    ) {
      console.error(
        `Call UUID [${this.guidedResults?.callUuid}] or Module Assignment UUID [${this.guidedResults?.moduleAssignmentUuid}] not found`
      );
      return;
    }
    this.assignmentService
      .getPracticeCallResults(
        this.guidedResults?.callUuid,
        this.guidedResults?.moduleAssignmentUuid
      )
      .subscribe({
        next: (dataResponse: IPracticeCallResult[]) => {
          this.practiceCallResults = dataResponse;
          this.practiceCallResults.forEach((result) => {
            result.attemptDate = this.localeService.formatDateTime(result.attemptDate);
          })

          this.isFetchingResults = false;
        },
        error: (err) => {
          console.error(
            'Error occurred in fetching practice call results: ',
            err
          );
          this.resultReady = false;
          this.isFetchingResults = false;
        },
        complete: () => {
          this.resultReady = true;
          this.isFetchingResults = false;
        },
      });
  }

  fetchAssignment(moduleAssignmentUuid: string): void {
    this.assignmentService
      .findByModuleAssignmentUuid(moduleAssignmentUuid)
      .subscribe({
        next: (dataResponse: IAssignmentModule | any) => {
          console.log('Assignment fetched successfully: ', dataResponse);
          this.assignment = dataResponse;
          this.call = this.assignment?.callsWithinModuleAssignnment.filter(
            (callModuleAssignment) =>
              callModuleAssignment.callUuid === this.guidedResults?.callUuid
          )[0];

          const calls = this.assignment?.callsWithinModuleAssignnment;
          if (calls?.length) {
            calls.forEach((call) => {
              if (call.callUuid === this.callUuid) {
                this.isMaxAttemptsReached =
                  this.callFlagService.isMaxAttemptsReached(call);
              }
            });
          }
        },
        error: (err) => {
          console.error('Error occurred in fetching assignment: ', err);
        },
      });
  }

  onSelectedPracticeTime(event: MatSelectChange) {
    this.resultReady = false;
    this.isUserCompliant = null;
    this.complianceScoringResult = undefined;
    const selectedValue = event.value;
    this.practiceCallUuid = selectedValue;
    console.log('Selected practice call UUID:', this.practiceCallUuid);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { practiceCallUuid: selectedValue },
      queryParamsHandling: 'merge'
    });

    this.pollData();
  }

  navigateToRoute() {
    if (this.practiceType === AppConstants.TYPE_PATHWAY) {
      this.router.navigate(['/learning-pathway'], {
        queryParams: {
          moduleAssignmentUuid: this.guidedResults?.moduleAssignmentUuid,
          practiceType: AppConstants.TYPE_PATHWAY,
        },
      });
      return;
    }

    let shouldCheckScore =
      this.practiceType === AppConstants.TYPE_CERTIFICATION ||
      this.criteriaScore > 0;

    if (shouldCheckScore && this.practiceScore >= this.criteriaScore) {
      this.router.navigate([`/assignments`]);
      return;
    }
    this.navigateToCall();
  }

  hasPassed(): boolean {
    if (
      this.practiceType === AppConstants.TYPE_CERTIFICATION ||
      this.criteriaScore > 0 ||
      (this.isPathwayFlow && this.criteriaScore === 0)
    ) {
      return this.practiceScore >= this.criteriaScore;
    }
    return false;
  }

  get returnButtonLabel(): string {
    if (this.practiceType === AppConstants.TYPE_PATHWAY) {
      return this.translationService.translate('RETURN_TO_PATHWAY', 'results');
    }
    return this.translationService.translate('RETURN_HOME', 'results');
  }

  private navigateToCall() {
      const returnUrl = `/call?moduleAssignmentUuid=${this.guidedResults?.moduleAssignmentUuid}&callUuid=${this.guidedResults?.callUuid}&practiceType=${this.guidedResults?.practiceCall?.practiceType}&callType=${this.guidedResults?.callType}`;
      localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));
      this.router.navigate([`/call`], {
        queryParams: {
          moduleAssignmentUuid: this.guidedResults?.moduleAssignmentUuid,
          callUuid: this.guidedResults?.callUuid,
          practiceType: this.guidedResults?.practiceCall?.practiceType,
          callType: this.guidedResults?.callType,
        },
      });
  }

  copyText() {
    this.clipboard.copy(this.guidedResults!.practiceCall.uuid);
    this.tooltipMenu.openMenu();

    setTimeout(() => {
      this.tooltipMenu.closeMenu();
    }, 3000);
  }

  isTextUnderscore(str: string): boolean {
    const regex = /^[ _]*$/;
    return regex.test(str);
  }

  checkPromptType() {
    if (this.guidedResults?.hasOwnProperty('promptTypes')) {
      if (this.guidedResults?.promptTypes?.includes('SCORE_SKILLS')) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  tabClick(tabIndex: number) {
    if (tabIndex == 1) {
      this.tabIndex = tabIndex;
      this.indicator = false;
    }
  }

  // displayIndicator(resultAvailable: boolean) {       /******* Not required temporarily *******/
  //   if(this.tabIndex != 1) {
  //     this.indicator = resultAvailable;
  //   }
  // }

  get getBreadcrumbs() {
    if (this.practiceType === AppConstants.TYPE_PATHWAY) {
      return [
        {
          text: this.translationService.translate('ASSIGNMENTS', 'results'),
          routerLink: '/assignments',
        },
        {
          text: this.pathwayName || this.translationService.translate('RETURN_TO_PATHWAY', 'results'),
          routerLink: '/learning-pathway',
          queryParams: {
            moduleAssignmentUuid: this.guidedResults?.moduleAssignmentUuid,
            practiceType: AppConstants.TYPE_PATHWAY,
          },
        },
      ];
    }
    return [
      {
        text: this.translationService.translate(
          'ASSIGNMENTS',
          'results'
        ),
        routerLink: '/assignments',
      },
      {
        text: this.assignment?.moduleAssignmentName || 'Assignment',
        routerLink: '/assignment',
        queryParams: {
          moduleAssignmentUuid: this.guidedResults?.moduleAssignmentUuid,
          practiceType: this.guidedResults?.practiceCall?.practiceType,
        },
      },
    ];
  }

  toggle(index: number): void {
    // If index is already in the expandedIndices array, remove it (collapse)
    const idx = this.expandedIndices.indexOf(index);
    if (idx > -1) {
      this.expandedIndices.splice(idx, 1);
    } else {
      this.expandedIndices.push(index); // Otherwise, add it (expand)
    }
  }

  isExpandedd(index: number): boolean {
    return this.expandedIndices.includes(index);
  }

  get allExpanded(): boolean {
    return this.expandedIndices.length === this.guidedResults?.questions.length;
  }

  toggleAll(): void {
    if (this.allExpanded) {
      this.expandedIndices = []; // Collapse all
    } else {
      this.expandedIndices =
        this.guidedResults?.questions.map((_, i) => i) || []; // Expand all
      this.amplitudeService.trackEvent('resultspage_click_expandall', {
        call_type: AppConstants.CALL_TYPE_GUIDED,
      });
    }
  }

  toggleAllInitially(): void {
    this.expandedIndices =
      this.guidedResults?.questions
        .map((q, i) => ({ question: q, index: i }))
        .filter((q) => q.question.practiceCallGuided.negativeContext.length > 0)
        .map((q) => q.index) || [];
  }

  openImageModal(imageSrc: string): void {
    this.dialog.open(ImageViewerComponent, {
      data: { imageSrc },
      panelClass: 'image-dialog',
      maxWidth: '90vw',
      autoFocus: false,
    });
  }

  getCallDetails(call: IAssignmentModuleCall | undefined) {
    // Sending the Event of Clicking on 'View Call Details' to Amplitude
    this.amplitudeService.trackEvent('assignmentpage_click_calldetails', {
      call_status: call?.callStatus,
      assignment_status: this.getAssignmentStatus(),
      assignment_type: this.assignment?.moduleAssignmentType,
    });

    const moduleAssignmentUuid = this.assignment?.moduleAssignmentUuid;

    if (call) {
      this.assignmentService
        .getCallDetails(call.callUuid, moduleAssignmentUuid)
        .subscribe({
          next: (res: ICallDetailModal) => {
            this.callDetails = res;
            this.openViewCallDetailsModal(call);
            console.log(
              `Response received successfully for call details: ${res}`
            );
          },
          error: (err: any) => {
            console.error('Error occurred in getting call details:  ', err);
          },
        });
    }
  }

  openViewCallDetailsModal(call: IAssignmentModuleCall) {
    const moduleAssignmentUuid = this.assignment?.moduleAssignmentUuid;
    const callType = call.callType;

    if (!this.callDetailsModalPortalOutlet.hasAttached()) {
      if (this.componentPortal === null) {
        this.componentPortal = new ComponentPortal(CallDetailsModalComponent);
      }
      const attachedComponentRef =
        this.callDetailsModalPortalOutlet.attachComponentPortal(
          this.componentPortal
        );
      attachedComponentRef.instance.callDetails = this.callDetails;
      attachedComponentRef.instance.moduleAssignmentUuid = moduleAssignmentUuid;
      attachedComponentRef.instance.callType = callType;
      attachedComponentRef.instance.practiceType = this.practiceType;
      attachedComponentRef.instance.call_status = call.callStatus;
      attachedComponentRef.instance.assignment_status =
        this.getAssignmentStatus();
      attachedComponentRef.instance.assignment_type =
        this.assignment?.moduleAssignmentType;
      attachedComponentRef.instance.isMaxAttemptsReached =
        this.isMaxAttemptsReached;

      this.scrollStrategy = this.overlay.scrollStrategies.block();
      this.scrollStrategy.enable();

      attachedComponentRef.instance.closeModalEvent
        .pipe(take(1))
        .subscribe(() => {
          this.callDetailsModalPortalOutlet.detach();
          this.scrollStrategy?.disable();
        });
    }
  }

  getAssignmentStatus(): string {
    if (this.assignment) {
      const assignmentStatusComplete =
        this.assignment.callsWithinModuleAssignnment.every(
          (call) =>
            call.callStatus === 'COMPLETE' || call.callStatus === 'CERTIFIED'
        );
      const assignmentStatusNotStarted =
        this.assignment.callsWithinModuleAssignnment.every(
          (call) => call.callStatus === 'NOT_STARTED'
        );
      const assignmentStatusOverdue =
        !assignmentStatusComplete &&
        this.assignment.moduleAssignmentDueDate &&
        this.assignmentDaysLeft !== null &&
        this.assignmentDaysLeft < 0;

      if (assignmentStatusOverdue) {
        return 'Overdue';
      } else if (assignmentStatusComplete) {
        return 'Complete';
      } else if (assignmentStatusNotStarted) {
        return 'Not Started';
      } else {
        return 'Incomplete';
      }
    }
    return 'Not Started';
  }

  preloadImages(): void {
    if (navigator.onLine) {
      if (
        !this.guidedResults?.visualAid ||
        !this.guidedResults?.visualAid?.fileCount ||
        !this.guidedResults?.visualAid?.baseUrl
      ) {
        console.error('Visual aid or file count or base URL is not defined.');
        return;
      }

      // Get guided results visual index for images
      const indices = this.imagePreloaderService.getVisualAidIndices(
        this.guidedResults?.questions
      );
      console.log('Unique Visual Aid Indices:', indices);

      const baseUrl = this.guidedResults.visualAid.baseUrl;
      this.imagePreloaderService
        .preloadImages(baseUrl, indices)
        .then((results: IPreloadedImageResult[]) => {
          console.log(`Successfully preloaded ${results.length} images`);
          this.visualAid = results.length > 0;
          this.imgArray = this.imagePreloaderService.getImageArray();

          this.setVisualAidSrc();
        })
        .catch((error) => {
          console.error('Error preloading images:', error);
          this.visualAid = false;
        });
    }
  }

  /**
   * Maps visual aid images to their corresponding questions based on visualAidIndex.
   * Each question with a valid visualAidIndex will have its visualAidSrc property
   * set to the matching image URL from the preloaded image array.
   */
  setVisualAidSrc(): void {
    // Validate that we have images to work with
    if (this.imgArray.length < 1) return;

    // Validate that we have guided results
    if (!this.guidedResults) return;

    // Validate that we have questions to process
    if (this.guidedResults.questions.length < 1) return;

    // Iterate through each question and assign the corresponding visual aid image
    this.guidedResults.questions.forEach((question) => {
      // Check if question has a valid visual aid index
      if (question.visualAidIndex !== undefined && question.visualAidIndex >= 0) {
        // Find the matching image by searching for the index in the filename
        const matchingImage = this.imgArray.find((imageUrl) =>
          imageUrl.includes(`${question.visualAidIndex}.`)
        );

        if (matchingImage) {
          question.visualAidSrc = matchingImage;
        }
      }
    });

    console.log('Visual Aid: Updated visual aid src on each question', {
      totalImages: this.imgArray.length,
      totalQuestions: this.guidedResults.questions.length,
      questions: this.guidedResults.questions
    });
  }

  private getActiveCompliancePromptType(): string {
    const types = this.guidedResults?.promptTypes ?? [];
    if (types.includes(AppConstants.COMPLIANCE_PROSECUTOR)) {
      return AppConstants.COMPLIANCE_PROSECUTOR;
    }
    return AppConstants.SCORE_COMPLIANCE;
  }

  getComplianceScore() {
    const promptType = this.getActiveCompliancePromptType();
    this.resultsConversationalService
      .getResultsPromptTypeData(this.practiceCallUuid, promptType)
      .subscribe({
        next: (res: any) => {
          if (res && res.contentDetails) {
            const data = JSON.parse(res.contentDetails);
            this.complianceScoringResult = data[Object.keys(data)[0]];
            this.anyCorrectFact = this.checkAnyCorrectFact(this.complianceScoringResult);
          }
        },
        error: (error) => {
          console.error('Error:', error);
        },
      });
  }

  private checkAnyCorrectFact(result: IComplianceScoring | undefined): boolean {
    return (
      !!result &&
      result.sections.some(section =>
        section.facts.some(fact => fact.correct === 1)
      )
    );
  }

  private loadCustomResultMessage(): void {
    this.accountFeatureService
      .getResultMessageText(AppConstants.ACCOUNT_FEATURE_CUSTOM_RESULT_MESSAGING)
      .subscribe((message) => (this.customResultMessage = message));
  }

  async checkTranscriptTabFeatureFlag() {
    const treatment = await this.splitIoService.getTreatment(
      AppConstants.TRANSCRIPT_TAB
    );
    const results = this.guidedResults;
    console.log('Transcript Tab Feature Treatment:    ', treatment);
    this.showTranscription = !!(treatment === 'on' && results?.promptTypes?.includes(this.promptTypeCleanTranscript));
  }
}
